// frontend/src/pages/PaymentHistory/helpers.ts
// Pure helpers: no React, no state, no DOM. Trivially unit-testable.

import type { Payment } from "./types";

/**
 * Format an amount as en-US USD-style with 2 decimals.
 * Accepts number, numeric string, or null/undefined.
 */
export function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === "number" ? v : parseFloat(String(v || 0));
  return "$" + n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format an ISO date as "Oct 4, 2026".
 * Returns empty string on invalid or missing input.
 */
export function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Convert a "YYYY-MM" period reference into "Month YYYY" (e.g. "August 2026").
 * Falls back to the raw string if it does not match the YYYY-MM pattern.
 */
export function fmtPeriod(sourceRef?: string | null): string {
  if (!sourceRef) return "";
  if (/^\d{4}-\d{2}$/.test(sourceRef)) {
    const [year, month] = sourceRef.split("-");
    const d = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }
  return sourceRef;
}

/**
 * Human-readable label for a payment's obligation (what the payment is FOR).
 */
export function obligationLabel(p: Payment): string {
  const map: Record<string, string> = {
    pd7a: "Payroll remittance",
    gst_hst: "GST/HST remittance",
    wcb: "WCB premium",
    payroll: "Direct deposit",
  };
  return map[p.source_type] || p.source_name || "Payment";
}

/**
 * Secondary line under the obligation label (period + optional PD7A tag).
 */
export function subLabel(p: Payment): string {
  const period = fmtPeriod(p.source_ref);
  if (p.source_type === "pd7a") {
    return period ? period + " · PD7A" : "PD7A";
  }
  return period || "";
}

/**
 * Human-readable label for the payment method / provider.
 */
export function methodLabel(p: Payment): string {
  if (p.provider === "manual") return "Manual";
  if (p.provider === "vopay") return "Auto-pay (VoPay)";
  if (p.provider === "plooto") return "Auto-pay (Plooto)";
  return p.provider || "Manual";
}